import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBusyAction } from '@/db/useBusyAction';
import { allEquipmentTypesState, equipmentTypeFamilyOfState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import {
    DEFAULT_EQUIPMENT_SPACE_ID,
    type EquipmentType,
    type EquipmentTypeId,
    isOfferableType,
    type RetiredTypeReference,
    type RetiredTypeReferenceKind,
    type RetireEquipmentTypeUsage,
    unmatchedInputFields,
} from '@rock/types';
import { groupBy, orderBy } from 'lodash';
import { type FC, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { formatMovedSummary, retireEquipmentType } from './retireApi';

const log = logger('RetireEquipmentTypeDialog');

type Mode = 'retire' | 'move';

/**
 * Retire a live catalog type, optionally moving what homeowners hold on it to another type. Opens on a
 * dry run so the admin sees what still uses the type before choosing.
 */
export const RetireEquipmentTypeDialog: FC<{
    type: EquipmentType;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onRetired?: () => void;
}> = ({ type, open, onOpenChange, onRetired }) => {
    const [mode, setMode] = useState<Mode>('retire');
    const [targetTypeId, setTargetTypeId] = useState<EquipmentTypeId>();
    const targets = useMoveTargets(type);
    const target = targets.find((t) => t.id === targetTypeId);
    const requestedTarget = mode === 'move' ? targetTypeId : undefined;
    const { usage, error } = useRetireUsage(open ? type.id : undefined);
    const unmatchedFields = target ? unmatchedInputFields(type, target) : [];
    const blockers = mode === 'move' ? (usage?.moveBlockedBy ?? []) : [];
    const moveBlocked = blockers.length > 0;
    const { busy, run } = useBusyAction(log, { typeId: type.id });

    async function confirm() {
        const done = await run('Retire', async () => {
            const result = await retireEquipmentType({
                typeId: type.id,
                ...(requestedTarget ? { targetTypeId: requestedTarget } : {}),
                dryRun: false,
            });
            toast.success(`Retired ${type.title}`, { description: formatMovedSummary(result.moved) || undefined });
            log.info('Retired equipment type', { typeId: type.id, targetTypeId: requestedTarget, moved: result.moved });
        });
        if (done) {
            onOpenChange(false);
            onRetired?.();
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Retire {type.title}</DialogTitle>
                    <DialogDescription>
                        Retiring takes {type.title} out of every list where a type is picked for something new.
                        Everything already on it keeps working, and you can restore it any time from the Retired tab.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-5 py-2 text-sm">
                    <UsageSummary usage={usage} error={error} />

                    <RadioGroup value={mode} onValueChange={(value) => setMode(value as Mode)} className="gap-4">
                        <div className="flex flex-row items-start gap-3">
                            <RadioGroupItem value="retire" id="retire-only" className="mt-0.5" />
                            <Label htmlFor="retire-only" className="flex flex-col items-start gap-1 font-normal">
                                <span className="font-medium">Retire only</span>
                                <span className="text-muted-foreground">
                                    Everything above stays on {type.title}. Homes' unfinished guide starters for it are
                                    closed out, and reopen if you restore it.
                                </span>
                            </Label>
                        </div>
                        <div className="flex flex-row items-start gap-3">
                            <RadioGroupItem
                                value="move"
                                id="retire-move"
                                className="mt-0.5"
                                disabled={targets.length === 0}
                            />
                            <Label htmlFor="retire-move" className="flex flex-col items-start gap-1 font-normal">
                                <span className="font-medium">Retire and move to another type</span>
                                <span className="text-muted-foreground">
                                    {targets.length === 0
                                        ? `No other ${type.scope} type in this space to move to.`
                                        : `Equipment, models, documents, recalls and guide starters move over. Guides stay with ${type.title}.`}
                                </span>
                            </Label>
                        </div>
                    </RadioGroup>

                    {mode === 'retire' && !!usage?.movable.equipment && (
                        <RetireOnlyEquipmentWarning type={type} canMove={targets.length > 0} />
                    )}

                    {moveBlocked && <MoveBlockedNotice type={type} blockers={blockers} />}

                    {mode === 'move' && !moveBlocked && (
                        <div className="flex flex-col gap-2 pl-7">
                            <Select
                                value={targetTypeId ?? ''}
                                onValueChange={(value) => setTargetTypeId(value as EquipmentTypeId)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Pick the type to move to" />
                                </SelectTrigger>
                                <SelectContent>
                                    {targets.map((t) => (
                                        <SelectItem key={t.id} value={t.id}>
                                            {t.title} <span className="text-muted-foreground">({t.id})</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {target && unmatchedFields.length > 0 && (
                                <p className="text-amber-700">
                                    {target.title} has no {unmatchedFields.map((f) => f.label).join(', ')} field. Those
                                    answers stay saved on the equipment but won't show under {target.title}.
                                </p>
                            )}
                            {target && (
                                <p className="text-muted-foreground">
                                    Restoring {type.title} later brings it back to the catalog. What moved stays on{' '}
                                    {target.title}.
                                </p>
                            )}
                        </div>
                    )}

                    {usage && usage.references.length > 0 && <ReferenceList references={usage.references} />}
                </div>

                <DialogFooter>
                    <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={confirm}
                        disabled={busy || !usage || moveBlocked || (mode === 'move' && !targetTypeId)}
                    >
                        {mode === 'move' && target ? `Retire and move to ${target.title}` : 'Retire'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const UsageSummary: FC<{ usage: RetireEquipmentTypeUsage | undefined; error: string | undefined }> = ({
    usage,
    error,
}) => {
    if (error) return <p className="text-destructive">Couldn't load what uses this type: {error}</p>;
    if (!usage) return <p className="text-muted-foreground">Checking what uses this type…</p>;
    const { movable, homes } = usage;
    const rows = [
        { label: `Equipment in ${homes} ${homes === 1 ? 'home' : 'homes'}`, count: movable.equipment },
        { label: 'Models', count: movable.models },
        { label: 'Documents', count: movable.documents },
        { label: 'Recalls', count: movable.recalls },
        { label: 'Guide starters', count: movable.starters },
    ];
    return (
        <div className="flex flex-col gap-1">
            <div className="font-medium">Using this type today</div>
            <ul className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-0.5">
                {rows.map((row) => (
                    <li key={row.label} className={`contents ${row.count ? '' : 'text-muted-foreground'}`}>
                        <span>{row.label}</span>
                        <span className="text-right tabular-nums">{row.count}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

/** Mirrors `recallCatalogTypes`, which leaves retired types out of recall classification. */
const RetireOnlyEquipmentWarning: FC<{ type: EquipmentType; canMove: boolean }> = ({ type, canMove }) => {
    const family = useAsyncAtomValue(equipmentTypeFamilyOfState(type.id));
    return (
        <p className="pl-7 text-amber-700">
            Recalls are never classified as a retired type, so the equipment left on {type.title} will only be reached
            by a recall listing its exact model number
            {family ? `, or one classified as another ${family.name} type (the owner is asked)` : ''}.
            {canMove && ' Choose Retire and move instead if another type fits.'}
        </p>
    );
};

const MoveBlockedNotice: FC<{ type: EquipmentType; blockers: RetiredTypeReference[] }> = ({ type, blockers }) => (
    <div className="flex flex-col gap-1 pl-7">
        <p className="text-destructive">
            {blockers.length === 1 ? 'An approved query names' : `${blockers.length} approved queries name`} {type.id}.
            Moved equipment would stop matching, so homes would lose the reminders and suggestions{' '}
            {blockers.length === 1 ? 'it gives' : 'they give'} them. Update {blockers.length === 1 ? 'it' : 'them'}{' '}
            first, or retire only.
        </p>
        <ul className="flex flex-col">
            {blockers.map((ref) => (
                <li key={`${ref.kind}:${ref.id}`}>
                    <ReferenceLink reference={ref} />
                </li>
            ))}
        </ul>
    </div>
);

const REFERENCE_KIND_LABELS: Record<RetiredTypeReferenceKind, string> = {
    'consideration-type': 'Considerations',
    'lesson-type': 'Lessons',
    'custom-reminder': 'Custom reminders',
    'system-part': 'Systems',
    guide: 'Guides',
    'test-library': 'Test library',
    'core-field': 'Core fields',
    'task-supplies': 'Task supplies',
};

/** Admin-authored content that names the type. A retire leaves it as is, so it's listed for a hand edit. */
const ReferenceList: FC<{ references: RetiredTypeReference[] }> = ({ references }) => {
    const byKind = groupBy(references, 'kind');
    return (
        <div className="flex flex-col gap-2">
            <div>
                <div className="font-medium">Also named in</div>
                <div className="text-muted-foreground">These keep the type as written. Update them by hand.</div>
            </div>
            {Object.entries(byKind).map(([kind, refs]) => (
                <div key={kind}>
                    <div className="text-xs uppercase text-muted-foreground">
                        {REFERENCE_KIND_LABELS[kind as RetiredTypeReferenceKind]}
                    </div>
                    <ul className="flex flex-col">
                        {refs.map((ref) => (
                            <li key={`${ref.kind}:${ref.id}`}>
                                <ReferenceLink reference={ref} />
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
};

const ReferenceLink: FC<{ reference: RetiredTypeReference }> = ({ reference }) => {
    const to = referencePath(reference);
    if (!to) return <span>{reference.title}</span>;
    return (
        <Link to={to} target="_blank" className="text-blue-700 hover:underline">
            {reference.title}
        </Link>
    );
};

function referencePath(reference: RetiredTypeReference): string | undefined {
    switch (reference.kind) {
        case 'consideration-type':
            return `/content/considerations/${reference.id}`;
        case 'lesson-type':
            return `/content/lessons/${reference.id}`;
        case 'system-part':
            return `/equipment/types/${reference.id}`;
        case 'guide':
            if (reference.spaceId === DEFAULT_EQUIPMENT_SPACE_ID) return '/equipment/default-guide';
            return reference.spaceId
                ? `/spaces/${reference.spaceId}/guides`
                : `/equipment/types/${reference.id}/guides`;
        case 'test-library':
            return '/equipment/library';
        case 'core-field':
            return '/equipment/core-fields';
        case 'task-supplies':
            return '/content/tasks';
        case 'custom-reminder':
            return undefined;
    }
}

/**
 * Live types a retire may move onto: same space and scope, still offered, not the type itself. None for a
 * system: it lives in homes as folders, which a move doesn't carry.
 */
function useMoveTargets(type: EquipmentType): EquipmentType[] {
    const allTypes = useAsyncAtomValue(allEquipmentTypesState, []);
    return useMemo(
        () =>
            type.scope === 'system'
                ? []
                : orderBy(
                      allTypes.filter(
                          (t) =>
                              t.id !== type.id &&
                              t.spaceId === type.spaceId &&
                              t.scope === type.scope &&
                              isOfferableType(t),
                      ),
                      (t) => t.title.toLowerCase(),
                  ),
        [allTypes, type.id, type.spaceId, type.scope],
    );
}

/** The dry-run usage report, fetched once per open. None of it depends on the move target. */
function useRetireUsage(typeId: EquipmentTypeId | undefined) {
    const [state, setState] = useState<{ usage?: RetireEquipmentTypeUsage; error?: string }>({});
    useEffect(() => {
        if (!typeId) return;
        let cancelled = false;
        setState({});
        retireEquipmentType({ typeId, dryRun: true })
            .then((result) => {
                if (!cancelled) setState({ usage: result.usage });
            })
            .catch((err) => {
                log.error('Failed to load retire usage', { err, typeId });
                if (!cancelled) setState({ error: err instanceof Error ? err.message : 'Unknown error' });
            });
        return () => {
            cancelled = true;
        };
    }, [typeId]);
    return state;
}
